import { css } from "@emotion/react"
import { useEffect, useState } from "react"

const activeEnv = import.meta.env.VITE_ACTIVE_ENV

type StaffTopBarProps = {
  personName: string
  personId: string | number
  organizationName: string | undefined
}

export function StaffTopBar({
  personName,
  personId,
  organizationName,
}: StaffTopBarProps) {
  const [isHidden, setIsHidden] = useState(false)

  function toggleVisibility() {
    setIsHidden((prev) => !prev)
  }

  useEffect(() => {
    function handleKeyUp(event: KeyboardEvent) {
      const target = event.target as HTMLElement
      const nodeName = target.nodeName.toLowerCase()
      if (nodeName === "input" || nodeName === "textarea") return
      if (target.contentEditable === "true") return

      if (event.key === "~") {
        toggleVisibility()
      }
    }

    window.addEventListener("keyup", handleKeyUp)
    return () => window.removeEventListener("keyup", handleKeyUp)
  }, [])

  function toggleEnvironment() {
    const { hostname, href } = window.location

    const isStaging = hostname.includes(".staging.")
    const base = `https://accounts${isStaging ? "" : "-staging"}.planningcenteronline.com`
    const impersonationPath = "/staff/church_center_impersonations/new"

    const currentUrl = new URL(href)

    // Toggle staging subdomain
    currentUrl.hostname = isStaging
      ? hostname.replace(".staging.", ".")
      : hostname.replace(/^([^.]*)\./, "$1.staging.")

    const url = new URL(impersonationPath, base)
    url.searchParams.set("person_id", String(personId))
    url.searchParams.set("return", currentUrl.toString())

    window.location.href = url.toString()
  }

  const showEnvironmentToggle =
    activeEnv === "staging" || activeEnv === "production"

  return (
    <div css={[styles.bar, isHidden && styles.barHidden]}>
      <button
        type="button"
        onClick={toggleVisibility}
        css={styles.toggle}
        data-toggle
      >
        <span style={{ fontSize: "8px" }}>{isHidden ? "▼" : "▲"}</span>
      </button>
      <span css={styles.badge}>staff-only</span>

      <span>
        Impersonating <strong>{personName}</strong> in{" "}
        <strong>{organizationName}</strong>
      </span>

      <a href="/logout" css={styles.link}>
        Stop impersonating
      </a>

      {showEnvironmentToggle && (
        <button
          type="button"
          onClick={toggleEnvironment}
          css={[
            styles.link,
            css`
              border: none;
              cursor: pointer;
            `,
          ]}
        >
          View on {activeEnv === "staging" ? "Production" : "Staging"}
        </button>
      )}
    </div>
  )
}

const styles = {
  bar: css`
    align-items: center;
    background-color: #353535;
    border-bottom: 2px solid rgba(0, 0, 0, 0.2);
    color: rgba(255, 255, 255, 0.7);
    display: flex;
    gap: 1rem;
    flex-shrink: 0;
    font-family: "Lato", sans-serif;
    padding: 1rem;
    position: relative;

    &:hover [data-toggle] {
      display: flex;
    }
  `,
  barHidden: css`
    width: 100%;
    left: 0;
    top: 0;
    position: absolute;
    transform: translateY(-100%);
    z-index: 1000;

    [data-toggle] {
      display: flex;
      padding-top: 0.5em;
    }
  `,
  toggle: css`
    background: #353535;
    border: 2px solid rgba(0, 0, 0, 0.2);
    color: rgba(255, 255, 255, 0.7);
    border-top: 0;
    padding: 0 1em 0.4em;
    line-height: 1;
    display: none;
    align-items: flex-start;
    overflow: hidden;
    position: absolute;
    left: 50%;
    bottom: 0;
    transform: translate3d(-50%, 100%, 0);
    z-index: 1001;
    cursor: pointer;
  `,
  badge: css`
    background: rgba(255, 255, 255, 0.3);
    border-radius: 4px;
    display: inline-block;
    font-weight: 600;
    padding: 6px;
    font-size: 10px;
    text-transform: uppercase;
  `,
  link: css`
    display: inline-flex;
    background: #747474;
    color: white;
    font-weight: 600;
    font-size: 12px;
    padding: 6px 12px;
    border-radius: 4px;
    text-decoration: none;
  `,
}
